import { FC, useMemo } from 'react'

import { PERCENTAGE_UNIT } from 'Automations/nodeDefinitions/conditions'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'

import { useCurrencyOptions } from '../hooks/useCurrencyOptions'

export type PriceUnitSelectorProps = {
  'data-testid'?: string
  value: string
  onChange: (unit: string) => void
  /** When true (default), every enabled account currency is listed. */
  allowCurrency?: boolean
  /** When true (default), `%` is offered as a unit option. */
  allowPercentage?: boolean
}

/**
 * Dropdown that lets the user pick a "price unit" — either a currency code
 * (drawn from the account's enabled currencies) or the percentage sentinel.
 * The two listings combine in `[%, ...currencies]` order so percentage
 * (when enabled) is the first option. Consumers store the selected value as
 * the `unit` field of an amount-with-unit shape.
 */
export const PriceUnitSelector: FC<PriceUnitSelectorProps> = ({
  'data-testid': dataTestId,
  value,
  onChange,
  allowCurrency = false,
  allowPercentage = false
}) => {
  const currencyOptions = useCurrencyOptions()

  const options = useMemo(() => {
    const percentage = allowPercentage
      ? [{ label: '%', value: PERCENTAGE_UNIT }]
      : []
    const currencies = allowCurrency ? currencyOptions : []
    return [...percentage, ...currencies]
  }, [allowCurrency, allowPercentage, currencyOptions])

  return (
    <InputSelectControlField
      data-testid={dataTestId}
      value={value ?? ''}
      onChange={selected => onChange(String(selected))}
      options={options}
    />
  )
}
